// v3.7.8: Full implementation — migrated from ChatSidebar.tsx
import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { createPortal } from 'react-dom';
import { Clock, Pin, Move, Trash2, X, FolderOpen } from 'lucide-react';
import { ConversationListItem } from '../ConversationListItem';
import { formatRelativeTime } from './utils';

interface ConversationListProps {
  conversations: any[]; filterMode: string; setFilterMode: (m: string) => void;
  activeConversationId: string | null; onSelectConversation: (id: string | null) => void;
  onDelete: (id: string) => void; onToggleStar: (id: string) => void; onTogglePin: (id: string) => void;
  selectionMode: boolean;
  setSelectionMode: (v: boolean) => void;
  setSelectedIds: (v: Set<string>) => void; selectedIds: Set<string>; toggleSelect: (id: string) => void;
  handleBatchDelete: () => void; handleBatchMove: (projectId: string | null) => void;
  batchMoveOpen: boolean; setBatchMoveOpen: (v: boolean) => void;
  projects: any[]; lang: 'en' | 'zh'; isZh: boolean;
  timeGroups: any[]; pinnedList: any[]; loading: boolean;
  searchQuery: string; searchConvIds: Set<string>;
  projectMap: Record<string, any>; baseList: any[];
  portalRoot: HTMLElement | null;
  handleAssignProject: (conversationId: string, projectId: string) => void;
}

export const ConversationList = React.memo(function ConversationList(props: ConversationListProps) {
  const { conversations, filterMode, setFilterMode, activeConversationId, onSelectConversation,
    onDelete, onToggleStar, onTogglePin, selectionMode, selectedIds, toggleSelect,
    handleBatchDelete, handleBatchMove, batchMoveOpen, setBatchMoveOpen,
    setSelectionMode, setSelectedIds, projects, lang, isZh, timeGroups, pinnedList, loading, searchQuery, baseList,
    projectMap, portalRoot, handleAssignProject } = props;

  const renderConvItem = (conv: any) => (
    <ConversationListItem
      key={conv.id} conversation={conv}
      isActive={activeConversationId === conv.id}
      onClick={() => onSelectConversation(conv.id)}
      onDelete={() => onDelete(conv.id)}
      formatTime={formatRelativeTime}
      isZh={isZh}
      projectName={conv.projectId ? projectMap[conv.projectId]?.name : undefined}
      projectColor={conv.projectId ? projectMap[conv.projectId]?.color : undefined}
      projects={projects} onAssignProject={handleAssignProject}
      starred={conv.starred} pinned={conv.pinned}
      onToggleStar={onToggleStar} onTogglePin={onTogglePin}
      selectionMode={selectionMode} selected={selectedIds.has(conv.id)}
      onToggleSelect={toggleSelect}
    />
  );

  return (
    <div className="flex-1 overflow-hidden flex flex-col min-h-0">
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={12} className="text-white/30" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-white/30">
            {isZh ? '任务记录' : 'Tasks'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {(['all','pinned','starred'] as const).map((key) => {
            const label = isZh ? ({all:'全部',pinned:'置顶',starred:'收藏'} as any)[key] : ({all:'All',pinned:'Pin',starred:'Star'} as any)[key];
            return (
              <button key={key} onClick={() => setFilterMode(key)}
                className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition-colors ${
                  filterMode === key ? 'bg-celestial-saturn/20 text-celestial-saturn' : 'text-white/55 hover:text-white/50'
                }`}
              >{label}</button>
            );
          })}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar px-2 pb-2">
        {loading && conversations.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-4 h-4 border-2 border-celestial-saturn/30 border-t-celestial-saturn rounded-full animate-spin" />
          </div>
        ) : baseList.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-white/55">
            {searchQuery ? (isZh ? '无匹配对话' : 'No matches') : (isZh ? '暂无对话记录' : 'No conversations yet')}
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {pinnedList.length > 0 && filterMode !== 'starred' && (
              <div className="mb-1">
                <div className="flex items-center gap-1 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-celestial-saturn/70">
                  <Pin size={10} /> {isZh ? '置顶' : 'Pinned'}
                </div>
                {pinnedList.map(renderConvItem)}
              </div>
            )}
            {timeGroups.map((g: any) => (
              <div key={g.key} className="mb-1">
                <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white/55">{g.label}</div>
                {g.items.map(renderConvItem)}
              </div>
            ))}
          </AnimatePresence>
        )}
      </div>
      {selectionMode && (
        <div className="border-t border-white/[0.06] px-3 py-2 flex items-center gap-2">
          <span className="text-[11px] text-white/40">{isZh ? '已选' : 'Sel'} {selectedIds.size}</span>
          <div className="flex-1" />
          <div className="relative">
            <button onClick={() => setBatchMoveOpen((v: boolean) => !v)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-white/60 hover:text-celestial-saturn hover:bg-white/[0.06] transition-colors"
            ><Move size={12} /> {isZh ? '移动' : 'Move'}</button>
            <AnimatePresence>
              {batchMoveOpen && portalRoot && createPortal(
                <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }} transition={{ duration: 0.15 }}
                  style={{ position: 'fixed', left: '1rem', bottom: '4.5rem', zIndex: 99999 }}
                  className="w-40 max-h-48 overflow-y-auto custom-scrollbar bg-[#0b1020] border border-white/10 rounded-xl shadow-xl p-1"
                >
                  <button onClick={() => handleBatchMove(null)} className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-white/50 hover:bg-white/[0.06]">
                    <FolderOpen size={12} /> {isZh ? '无项目' : 'None'}
                  </button>
                  {projects.map((p: any) => (
                    <button key={p.id} onClick={() => handleBatchMove(p.id)} className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-white/70 hover:bg-white/[0.06]">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                      <span className="truncate">{p.name}</span>
                    </button>
                  ))}
                </motion.div>, portalRoot
              )}
            </AnimatePresence>
          </div>
          <button onClick={handleBatchDelete} className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 size={13} /></button>
          <button onClick={() => { setSelectionMode(false); setSelectedIds(new Set()); }} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-colors"><X size={13} /></button>
        </div>
      )}
    </div>
  );
});
